import React, { useEffect } from "react";
import {
  SidebarGroup,
  SidebarGroupContent,
  SidebarMenu,
  SidebarMenuItem,
  useSidebar,
} from "@/components/ui/sidebar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { ActivityIcon } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import { useDashboardStore } from "@/stores";
import { useUsageLimit } from "@/hooks/UsageLimitContext";
import type { Usage } from "@/types/types";
import ClientFunds from "./client-funds";
import {
  ActivatedCreatorsDialog,
  CampaignsUsageDialog,
} from "./usage-dialogs";

const ALERT_DISMISSED_KEY = "usage_alert_dismissed";

// Module-level on purpose: defined inside PlanCardSidebar it would be a new
// component type on every render, remounting the subtree and resetting the
// dialogs' open state (they snapped shut on every background usage poll).
const PlanContent = ({ usage }: { usage: Usage }) => (
  <div className="flex flex-col gap-0.5">
    <ActivatedCreatorsDialog usage={usage.activated_creators} />
    <CampaignsUsageDialog usage={usage.campaigns} />
    <ClientFunds />
  </div>
);

const getUsagePercentage = (current: number, limit: number) =>
  Math.min((current / limit) * 100, 100);

const PlanCardSidebar: React.FC = () => {
  const { state } = useSidebar();
  const isCollapsed = state === "collapsed";
  const { usage, usageLoading } = useDashboardStore();
  const { showLimitAlert } = useUsageLimit();

  useEffect(() => {
    if (!usage?.activated_creators || usageLoading) return;

    const { current, limit } = usage.activated_creators;
    const pct = getUsagePercentage(current, limit);
    const key = `${current}:${limit}`;

    if (pct >= 90 && localStorage.getItem(ALERT_DISMISSED_KEY) !== key) {
      localStorage.setItem(ALERT_DISMISSED_KEY, key);
      showLimitAlert({
        feature: "Activated Creators",
        current,
        limit,
        ...(pct < 100 && {
          close_to_full: true,
          text: "Once you reach your limit, Amy will stop responding to creators. Either upgrade your plan or wait for limits to reset.",
        }),
      });
    }
  }, [usage, usageLoading]);

  if (!usage) return null;

  return (
    <>
      <Separator className="group-data-[variant=inset]:mx-2 group-data-[variant=inset]:w-auto!" />
      <SidebarGroup>
        <SidebarGroupContent>
          <SidebarMenu>
            <SidebarMenuItem>
              {isCollapsed ? (
                <Popover>
                  <PopoverTrigger asChild>
                    <Button variant="ghost" size="icon" className="size-8">
                      <ActivityIcon />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-64 p-2 rounded-xl" side="right" align="center">
                    <PlanContent usage={usage} />
                  </PopoverContent>
                </Popover>
              ) : (
                <div className="flex w-full flex-col gap-2 rounded-lg p-0.5 text-left text-sm">
                  <PlanContent usage={usage} />
                </div>
              )}
            </SidebarMenuItem>
          </SidebarMenu>
        </SidebarGroupContent>
      </SidebarGroup>
      <Separator className="group-data-[variant=inset]:mx-2 group-data-[variant=inset]:w-auto!" />
    </>
  );
};

export default PlanCardSidebar;